import { Button, toast } from "@devhop/ui";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { PlusIcon } from "lucide-react";
import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { useActionPermission } from "@/modules/auth/presentation/model/useActionPermission";
import type { FilterConditionDTO, OffsetPageQueryDTO } from "@/shared/contracts/base";
import { Breadcrumb } from "@/shared/ui/Breadcrumb";
import type { EventCalendarLayerDTO } from "../../domain/contracts/event-calendar";
import { formatCalendarMonthLabel } from "../lib/event-calendar-ui";
import { useDeleteEvent, useEventsQuery } from "../api/queries";
import { EventFilter } from "../ui/EventFilter";
import { EventTable } from "../ui/EventTable";
import { EventViewToggle } from "../ui/EventViewToggle";
import { PerformanceEventCalendar } from "../ui/PerformanceEventCalendar";

const DEFAULT_SORT = [{ field: "eventDate", dir: "asc" as const }];

export type EventsPageSearch = OffsetPageQueryDTO & {
  view?: "list" | "calendar";
  calendarLayer?: EventCalendarLayerDTO;
  calendarMonth?: string;
};

export function EventsPage() {
  const nav = useNavigate({ from: "/app/performance/events" });
  const search = useSearch({
    from: "/app/performance/events",
  }) as EventsPageSearch;
  const sort = search.sort?.length ? search.sort : DEFAULT_SORT;
  const view = search.view ?? "list";
  const calendarLayer = search.calendarLayer ?? "events";
  const calendarMonth =
    search.calendarMonth ?? formatCalendarMonthLabel(new Date());
  const filters = (search.filters as FilterConditionDTO[] | undefined) ?? [];

  const list = useEventsQuery({
    offset: search.offset,
    limit: search.limit,
    sort,
    filters: search.filters,
  });

  const canCreate = useActionPermission(["performance-events:create"]);
  const deleteEvent = useDeleteEvent();

  const updateSearch = (patch: Partial<EventsPageSearch>) => {
    nav({ search: { ...search, ...patch, offset: patch.offset ?? 0 } });
  };

  return (
    <>
      <Header>
        <Breadcrumb
          items={[
            { label: "ແຜງຄວບຄຸມ", to: "/app/dashboard" },
            { label: "ງານສະແດງ" },
          ]}
        />
      </Header>

      <Main>
        <div className="mb-2 flex flex-wrap items-center justify-between space-y-2 gap-3">
          <div>
            <h2 className="flex items-center font-bold text-2xl tracking-tight">
              ງານສະແດງ
            </h2>
            <p className="text-muted-foreground">ຈັດການງານສະແດງທັງໝົດໃນລະບົບ.</p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <EventViewToggle
              view={view}
              onChange={(nextView) => updateSearch({ view: nextView })}
            />
            {canCreate && (
              <Button
                onClick={() => nav({ to: "/app/performance/events/create" })}
              >
                <PlusIcon className="h-4 w-4" /> ສ້າງງານໃໝ່
              </Button>
            )}
          </div>
        </div>

        <div className="flex flex-col rounded-xl border bg-card pt-2">
          <EventFilter hideDateRange={view === "calendar"} />

          {view === "calendar" ? (
            <div className="p-2 pb-4">
              <PerformanceEventCalendar
                filters={filters}
                calendarMonth={calendarMonth}
                calendarLayer={calendarLayer}
                onCalendarMonthChange={(month) =>
                  updateSearch({ calendarMonth: month })
                }
                onCalendarLayerChange={(layer) =>
                  updateSearch({ calendarLayer: layer })
                }
              />
            </div>
          ) : (
            <EventTable
              data={list.data?.data ?? []}
              isLoading={list.isLoading}
              offset={search.offset ?? 0}
              limit={search.limit ?? 20}
              totalCount={list.data?.meta?.total ?? 0}
              onPaginationChange={(offset, limit) =>
                nav({ search: { ...search, offset, limit } })
              }
              sortBy={sort[0]?.field}
              sortOrder={sort[0]?.dir}
              onSortingChange={(id, desc) =>
                nav({
                  search: {
                    ...search,
                    sort: [{ field: id, dir: desc ? "desc" : "asc" }],
                  },
                })
              }
              onView={(event) =>
                nav({
                  to: "/app/performance/events/$id",
                  params: { id: event.id },
                })
              }
              onEdit={(event) =>
                nav({
                  to: "/app/performance/events/$id/edit",
                  params: { id: event.id },
                })
              }
              onDelete={(id) => {
                toast.promise(deleteEvent.run(id), {
                  loading: "ກຳລັງລຶບ...",
                  success: "ລຶບງານສະແດງສຳເລັດ",
                  error: "ລຶບງານສະແດງລົ້ມເຫຼວ",
                });
              }}
            />
          )}
        </div>
      </Main>
    </>
  );
}
